// mapLoader.js - 单例模式封装高德地图加载
import AMapLoader from '@amap/amap-jsapi-loader';

// 存储地图加载状态和实例
let mapLoaderPromise = null;
let isLoaded = false;

// 单例加载函数：确保地图脚本只加载一次
export const loadAMap = () => {

  // 1. 配置安全密钥（必须在 load 前）
  window._AMapSecurityConfig = {
    securityJsCode: 'f5e79b0d1d174ab1279710caca9492b9', // 替换为实际安全密钥
  };

  if (isLoaded) {
    // 已加载过，直接返回 Promise
    return Promise.resolve(window.AMap);
  }

  if (!mapLoaderPromise) {
    // 首次加载，创建加载 Promise
    mapLoaderPromise = AMapLoader.load({
      key: '4299039ca746fb39147d3839855f6943', // 高德地图 API 密钥 新 4299039ca746fb39147d3839855f6943  旧 02421fe2279f67b5058bbc78d96f258b
      version: '1.4.6', // 地图版本 
      plugins: ['AMap.ToolBar', 'AMap.Autocomplete', 'AMap.Geocoder', 'AMap.Geolocation', 'AMap.PlaceSearch', ], // 按需加载插件
    }).then((AMap) => {
      isLoaded = true;
      return AMap;
    }).catch((err) => {
      console.error('地图加载失败:', err);
      mapLoaderPromise = null; // 加载失败时重置，允许重试
      throw err;
    });
  }

  return mapLoaderPromise;
};

/**
 * 1.0 版本兼容的 getMeterPerPixel 替代方法
 * @param {AMap.Map} map - 地图实例
 * @returns {number} 每个像素对应的米数
 */
function getMeterPerPixelFallback(map) {
  // 获取中心点经纬度
  const center = map.getCenter();
  // 将中心点转换为容器像素坐标
  const pixel = map.lngLatToContainer(center);
  // 向右移动1像素的坐标
  const pixelRight = new AMap.Pixel(pixel.x + 1, pixel.y);
  // 将像素坐标转回经纬度
  const lngLatRight = map.containerToLngLat(pixelRight);

  // 计算两点距离（米）
  return AMap.GeometryUtil.distance(center, lngLatRight);
}

// 计算缩放级别的核心方法（适配1.0版本）
export const calculateZoom = (distance, center, mapInstancesRef) => {
    const { map, AMap } = mapInstancesRef;
   const [lng, lat] = center;
    if (!map) return;
    if (!lng || !lat) return;
    // 参数校验
    if (!center || center.length !== 2) {
      throw new Error('无效的中心点经纬度，必须是 [经度, 纬度] 格式');
    }
    if (typeof distance !== 'number' || distance <= 0) {
      throw new Error('公里数必须是正数');
    }

    try {
      const [lng, lat] = center;
      const distanceMeter = distance * 1000; // 转换为米

      // 1.0版本特性：使用getMeterPerPixel方法
      const meterPerPixel = getMeterPerPixelFallback(map);
  
      if (typeof meterPerPixel !== 'number') {
        throw new Error('获取像素距离失败，地图可能未初始化完成');
      }

      // 获取地图容器尺寸（预留10%边距）
      const container = map.getContainer();
      const containerWidth = container.clientWidth * 0.9;
      const containerHeight = container.clientHeight * 0.4;
      // const containerWidth = mapSize?.width * 0.9;
      // const containerHeight = mapSize?.height * 0.9;
      // console.log(container.clientWidth, container.clientHeight);
      
      // 计算覆盖指定距离所需的像素数
      const requiredPixelWidth = distanceMeter / meterPerPixel;
      const requiredPixelHeight = distanceMeter / meterPerPixel;

      // 计算缩放级别调整量（基于1.0版本的缩放特性）
      const zoomDiffWidth = Math.log(containerWidth / requiredPixelWidth) / Math.log(2);
      const zoomDiffHeight = Math.log(containerHeight / requiredPixelHeight) / Math.log(2);
      const zoomDiff = Math.min(zoomDiffWidth, zoomDiffHeight);

      // 计算目标缩放级别
      let targetZoom = map.getZoom() + zoomDiff;

      // 限制在1.0版本支持的缩放范围（3-18）
      targetZoom = Math.max(3, Math.min(18, Math.round(targetZoom)));

      // 应用缩放级别并居中
      map.setZoomAndCenter(targetZoom, center);

        //获取应用后的实际边界
      const actualBounds = map.getBounds();
      
      // 计算实际视野宽度（公里）
      const actualWidth =  AMap.GeometryUtil.distance(
        [actualBounds.getSouthWest().lng, lat],
        [actualBounds.getNorthEast().lng, lat]
      ) / 1000;

      // 计算实际视野高度（公里）
      const actualHeight = AMap.GeometryUtil.distance(
        [lng, actualBounds.getSouthWest().lat],
        [lng, actualBounds.getNorthEast().lat]
      ) / 1000;

      // 取宽度和高度的平均值作为实际覆盖范围
      const actualRange = (actualWidth + actualHeight) / 2;

      // 偏差率超过10%则调整
      const deviation = Math.abs(actualRange - distance) / distance;

      if (deviation > 0.1) {
        // 根据偏差方向调整（实际范围过大则缩小，过小则放大）
        targetZoom = actualRange > distance ? Math.floor(targetZoom) : Math.ceil(targetZoom);

        // 限制缩放级别在有效范围内（3-18）
        targetZoom = Math.max(3, Math.min(18, targetZoom));

        // 再次应用调整后的缩放级别
        map.setZoomAndCenter(targetZoom, center);
      }

      // message.success(`计算完成，缩放级别：${targetZoom}`);
    } catch (error) {
      console.error('计算失败:', error);
      // message.error('计算缩放级别失败，请重试');
    } 
  };

/**
 * 校验经纬度是否合法
 * @param {[number, number]} position - 经纬度数组 [经度, 纬度]
 * @returns {boolean} 是否合法
 */
export function isValidLngLat(position) {
  // 基础类型校验
  if (!Array.isArray(position) || position.length !== 2) {
    console.error('经纬度格式错误，必须是长度为2的数组 [经度, 纬度]');
    return false;
  }

  const [longitude, latitude] = position;
  
  // 数据类型校验
  if (typeof longitude !== 'number' || typeof latitude !== 'number' || isNaN(longitude) || isNaN(latitude)) {
    console.error('经纬度必须是有效的数字');
    return false;
  }

  // 数值范围校验（中国地区大致范围）
  const lngValid = longitude >= 73 && longitude <= 135; // 经度范围
  const latValid = latitude >= 18 && latitude <= 53;  // 纬度范围

  if (!lngValid) {
    console.error(`经度${longitude}超出范围（73-135）`);
  }
  if (!latValid) {
    console.error(`纬度${latitude}超出范围（18-53）`);
  }

  return lngValid && latValid;
}

// 销毁地图相关资源
export const destroyMap = (mapInstancesRef) => {
    const { map, marker, geocoder, eventListeners } = mapInstancesRef;

    // 1. 移除事件监听
    if(eventListeners){
      eventListeners.forEach(({ target, type, handler }) => {
        if (target.removeEventListener) {
          target.removeEventListener(type, handler);
        } else if (target.off) { // 高德实例的事件移除方法
          target.off(type, handler);
        }
      });
    }

    // 2. 移除标记点等覆盖物
    if (map && marker) {
      Object.values(mapInstancesRef).forEach(marker => {
        map.remove(marker);
      });
    }

    // 3. 销毁地理编码等插件实例（如果有destroy方法）
    if (geocoder && geocoder.destroy) {
      geocoder.destroy();
    }

    // 4. 销毁地图实例
    if (map && map.destroy) {
      map.destroy(); // 高德地图实例的销毁方法
    }

    // 5. 重置实例存储
    // mapInstances.current = {
    //   map: null,
    //   marker: null,
    //   geocoder: null,
    //   eventListeners: []
    // };

    // setIsMapLoaded(false);
    // console.log('地图资源已销毁');
  }
